// =============================================================================
// io/sync/userContracts.ts — the trader's own instruments.
// -----------------------------------------------------------------------------
// The shared `contracts` table next door is admin-seeded and world-readable with
// no write policy. This one is the opposite in both directions: private to its
// owner, and the only contract rows anybody in this app can write.
//
// ── ⚠ WHY A FAILED READ HERE DOES *NOT* THROW, WHERE THE SHARED ONE DOES ──
//
// `loadContracts` refuses to return an empty catalogue, because there is no such
// thing as a user with no contracts — an empty shared list means the read broke,
// and every consumer degrades to a plausible $0 rather than to a visible gap.
//
// Here, empty is the normal case: almost every trader has no custom instruments
// at all. So an empty array cannot be treated as a fault, and the failure has to
// be carried alongside it instead — hence `{ contracts, error }` rather than a
// throw. The caller shows the shared catalogue and says the custom list could
// not be read, which is strictly more usable than refusing to price anything.
//
// 🚨 AND THE ONE PLACE THAT DISTINCTION BITES: a failed read here means a
// trader's OVERRIDES are missing, so their instrument prices at the shared
// row's numbers instead of their own. That is a wrong number, not a missing
// one — which is why the error is surfaced on screen rather than swallowed.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { UserContract } from '../../domain/userContracts';
import { USER_CONTRACT_LIMIT } from '../../domain/userContracts';
import { reportFailureAs } from '../diagnostics/errorLog';

export const USER_CONTRACTS_SELECT =
  'symbol,name,exchange,tick_size,dollars_per_tick,dollars_per_point';

/**
 * ⚠ `Number(...)` with a finite check and NO null fallback, unlike the shared
 * catalogue's reader. Those columns are `NOT NULL CHECK (> 0)` in this table, so
 * a non-finite value here means the row is corrupt rather than incomplete — and
 * a corrupt spec is dropped by `userContractFromDb`'s caller rather than
 * returned with a hole in it.
 */
function pos(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** A row, or null when it could not be a usable spec. */
export function userContractFromDb(
  r: Record<string, unknown>,
): UserContract | null {
  const symbol = String(r['symbol'] ?? '').toUpperCase().trim();
  const tickSize = pos(r['tick_size']);
  const dollarsPerTick = pos(r['dollars_per_tick']);
  const dollarsPerPoint = pos(r['dollars_per_point']);
  if (!symbol || tickSize === null || dollarsPerTick === null || dollarsPerPoint === null) {
    return null;
  }
  return {
    symbol,
    name: String(r['name'] ?? '') || symbol,
    exchange: String(r['exchange'] ?? ''),
    tickSize,
    dollarsPerTick,
    dollarsPerPoint,
  };
}

export interface UserContractsRead {
  contracts: UserContract[];
  /** Non-null when the read failed. The list is then empty and NOT trustworthy. */
  error: string | null;
}

/**
 * Every instrument this trader added or overrode.
 *
 * ⚠ Unpaged, like the shared catalogue, and capped for the same reason — a
 * silently truncated list would apply SOME of a trader's overrides and not
 * others, which is worse than applying none. The cap is enforced on the way in
 * (`validateUserContract`); this asserts it on the way out.
 */
export async function loadUserContracts(
  sb: SupabaseClient,
): Promise<UserContractsRead> {
  // ⚠ CANNOT THROW, and that is not defensive padding. The shared catalogue's
  // reader throws on purpose, and its caller treats a throw as "the app has no
  // prices". A transport-level failure here — offline, a client that never
  // reached the network — must NOT take that path: the shared catalogue is
  // already loaded and every trade in a standard instrument still prices
  // correctly. Losing that over a missing override would be the larger outage.
  let data: unknown;
  let error: { message: string } | null = null;
  try {
    const res = await sb
      .from('user_contracts')
      .select(USER_CONTRACTS_SELECT)
      .order('symbol', { ascending: true })
      .limit(USER_CONTRACT_LIMIT + 1);
    data = res.data;
    error = res.error;
  } catch (e) {
    error = { message: e instanceof Error ? e.message : String(e) };
  }

  if (error) {
    return {
      contracts: [],
      // ⚠ Through the reporter, like every other user-facing failure — and this
      // one earns it twice over. The Contract Library screen says the overrides
      // are missing, but the NUMBERS on every other screen quietly become the
      // standard ones, with nothing there to say why. The activity log is where
      // that gets connected to a time and a cause.
      error: reportFailureAs(
        'custom contracts read', error,
        `Your custom instruments could not be read (${error.message}). Instruments you ` +
          `added are missing, and any you re-priced are showing the standard numbers instead.`,
      ),
    };
  }

  const rows = (data ?? []) as unknown as Record<string, unknown>[];
  if (rows.length > USER_CONTRACT_LIMIT) {
    return {
      contracts: [],
      error: reportFailureAs(
        'custom contracts read', new Error(`${rows.length} rows, over the limit`),
        `Your custom instrument list is above the ${USER_CONTRACT_LIMIT}-row limit, so it ` +
          `cannot be read whole. Applying part of it would price some of your instruments ` +
          `with your numbers and the rest with the standard ones.`,
      ),
    };
  }

  const out: UserContract[] = [];
  for (const r of rows) {
    const c = userContractFromDb(r);
    if (c) out.push(c);
  }
  return { contracts: out, error: null };
}

/**
 * Write one instrument. An upsert, so re-adding a symbol edits it.
 *
 * ⚠ `dollars_per_point` IS SENT, not left to the database to derive. It is
 * derived — from tick size and tick value, in `validateUserContract` — but the
 * derivation belongs to one function that is tested, not to two implementations
 * that can disagree. A generated column here would be the second one.
 */
export async function saveUserContract(
  sb: SupabaseClient, userId: string, c: UserContract,
): Promise<{ ok: boolean; error: string | null }> {
  const { error } = await sb.from('user_contracts').upsert({
    user_id: userId,
    symbol: c.symbol,
    name: c.name,
    exchange: c.exchange,
    tick_size: c.tickSize,
    dollars_per_tick: c.dollarsPerTick,
    dollars_per_point: c.dollarsPerPoint,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id,symbol' });

  if (error) {
    return {
      ok: false,
      error: reportFailureAs(
        'custom contract save', error,
        `“${c.symbol}” could not be saved: ${error.message}`,
      ),
    };
  }
  return { ok: true, error: null };
}

/**
 * Remove one instrument.
 *
 * 🚨 THE TRADES ARE NOT TOUCHED, AND THAT IS WHY THE SCREEN WARNS. Deleting a
 * custom instrument does not delete anything the trader imported under it — but
 * every one of those trades loses its $/point, so their excursion figures read
 * $0 and their tick- and R-denominated values drop out of totals. Silently.
 *
 * ⚠ Deleting an OVERRIDE is different and much safer: the shared row underneath
 * comes back, so the trades re-price to the standard numbers rather than to
 * nothing. The editor says which of the two is about to happen.
 */
export async function deleteUserContract(
  sb: SupabaseClient, userId: string, symbol: string,
): Promise<{ ok: boolean; error: string | null }> {
  const { error } = await sb
    .from('user_contracts')
    .delete()
    .eq('user_id', userId)
    .eq('symbol', symbol);

  if (error) {
    return {
      ok: false,
      error: reportFailureAs(
        'custom contract delete', error,
        `“${symbol}” could not be removed: ${error.message}`,
      ),
    };
  }
  return { ok: true, error: null };
}
